import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CONTAINER, HERO_DEFAULTS } from "@/components/home/data";
import { useBlock, useExperiment } from "@/lib/content";
import { absoluteUrl } from "@/lib/api";

// Fields an A/B variant may override on the hero (empty values are ignored, so
// a variant only changes what it sets — everything else stays as-is).
const HERO_AB_FIELDS = ["badge", "headingPre", "headingHighlight", "headingPost", "sub", "primaryLabel", "primaryHref"];

export const HomeHero = () => {
  const home = useBlock("home", null);
  const ov = (home && typeof home.hero === "object" && home.hero) || {};
  // Optional A/B test on the hero. Null unless an "hero" experiment is active,
  // so by default the hero is exactly the CMS/built-in content.
  const variant = useExperiment("hero");
  const abOv = {};
  if (variant) HERO_AB_FIELDS.forEach((k) => { if (typeof variant[k] === "string" && variant[k].trim()) abOv[k] = variant[k]; });
  const h = { ...HERO_DEFAULTS, ...ov, ...abOv };
  const features = Array.isArray(ov.features) && ov.features.length ? ov.features : HERO_DEFAULTS.features;
  const img = h.image ? absoluteUrl(h.image) : HERO_DEFAULTS.image;
  return (
    <section data-testid="page-hero" className="relative overflow-hidden bg-slate-50">
      <div className="absolute inset-0 cli-grid-bg" />
      <div className={`${CONTAINER} relative grid lg:grid-cols-2 gap-10 lg:gap-6 items-center pt-14 pb-20 lg:pt-16 lg:pb-24`}>
        <div>
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-pink-200 bg-pink-50 px-3.5 py-1.5 text-xs font-semibold tracking-wide text-purple-950">
              <span className="h-1.5 w-1.5 rounded-full bg-pink-400" />
              <span className="font-cli uppercase tracking-[0.15em]">{h.badge}</span>
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-[58px] font-bold tracking-tight text-slate-900 leading-[1.05]">
              {h.headingPre}{h.headingHighlight && <span className="text-pink-500">{h.headingHighlight}</span>}{h.headingPost}
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
              {h.sub}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to={h.primaryHref || "/managed-it"} data-testid="hero-primary-btn"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-900 px-7 py-3.5 text-[15px] font-semibold text-white shadow-lg shadow-purple-900/25 hover:bg-purple-950 transition-colors">
                {h.primaryLabel} <ArrowRight className="h-4 w-4" />
              </Link>
              <a href={h.secondaryHref || "#contact"} data-testid="hero-secondary-btn"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-7 py-3.5 text-[15px] font-semibold text-slate-900 hover:bg-slate-100 transition-colors">
                {h.secondaryLabel}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-9 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
              {features.map((f) => (
                <span key={f} className="inline-flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-purple-900" /> {f}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="relative flex items-center justify-center min-h-[340px] lg:min-h-[520px]">
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-[300px] w-[300px] lg:h-[440px] lg:w-[440px] rounded-full bg-pink-300/40 blur-[90px]" />
          </div>
          {/* Built-in hero ships AVIF (~58% smaller) with WebP fallback and
              responsive variants; a CMS-set image renders as a plain img. */}
          <picture>
            {img === HERO_DEFAULTS.image && (
              <source
                type="image/avif"
                srcSet="/brain-sm.avif 640w, /brain-hero.avif 1080w"
                sizes="(min-width: 1024px) 520px, (min-width: 640px) 440px, 90vw"
              />
            )}
            <img
              src={img}
              {...(img === HERO_DEFAULTS.image
                ? { srcSet: "/brain-sm.webp 640w, /brain-hero.webp 1080w",
                    sizes: "(min-width: 1024px) 520px, (min-width: 640px) 440px, 90vw" }
                : {})}
              alt={h.imageAlt || HERO_DEFAULTS.imageAlt}
              width="760"
              height="760"
              loading="eager"
              fetchpriority="high"
              className="relative h-auto w-full max-w-[440px] lg:max-w-[520px] object-contain animate-float select-none"
              draggable="false"
            />
          </picture>
        </Reveal>
      </div>
    </section>
  );
};
